import * as React from "react";
import clsx from "clsx";
import { Braces, Inbox, Mail, MessageSquare, RefreshCw, Trash2 } from "lucide-react";
import { JsonView, darkStyles } from "react-json-view-lite";
import "react-json-view-lite/dist/index.css";
import { Text } from "../../../ui/components/Text";
import { Callout } from "../../../ui/components/Callout";
import { Button } from "../../../ui/components/Button";
import { TabSwitcher } from "../../../ui/components/TabSwitcher";
import { CollapsibleSection } from "../../../ui/components/CollapsibleSection";
import { Modal } from "../../../ui/components/Modal";
import { DetailPageLayout } from "../layouts/DetailPageLayout";
import { useSectionCollapse } from "../../hooks/useSectionCollapse";
import { useI18n } from "../../lib/i18n";
import {
  clearCapturedEmails,
  clearCapturedSms,
  getCapturedEmails,
  getCapturedSms,
  type CapturedEmail,
  type CapturedSms,
} from "../../lib/api/auth";
import { vars } from "../../../ui/styles/theme.css";
import * as s from "./SystemCapturePage.css";

type CaptureTab = "email" | "sms";

const CAPTURE_COLLAPSE_SCOPE = "system-capture";
const MESSAGES_SECTION_ID = "messages";

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function formatPhone(phone: string): string {
  const digits = phone.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return phone;
}

function truncateBody(body: string, max = 60): string {
  return body.length > max ? `${body.slice(0, max)}…` : body;
}

function messageKey(sentAt: string, idx: number): string {
  return `${sentAt}-${idx}`;
}

export function SystemCapturePage() {
  const { t } = useI18n();
  const { isExpanded, toggle, bindSectionLayout } = useSectionCollapse(CAPTURE_COLLAPSE_SCOPE);

  const [tab, setTab] = React.useState<CaptureTab>("email");
  const [emails, setEmails] = React.useState<CapturedEmail[]>([]);
  const [sms, setSms] = React.useState<CapturedSms[]>([]);
  const [selectedEmailKey, setSelectedEmailKey] = React.useState<string | null>(null);
  const [selectedSmsKey, setSelectedSmsKey] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [metaOpen, setMetaOpen] = React.useState(false);
  const [refreshing, setRefreshing] = React.useState(false);

  const fetchAll = React.useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    const errors: string[] = [];
    try {
      const emailRes = await getCapturedEmails();
      setEmails(emailRes.emails ?? []);
    } catch (err: unknown) {
      errors.push(
        err instanceof Error ? err.message : t("pro.system.capture.error.load"),
      );
    }
    try {
      const smsRes = await getCapturedSms();
      setSms(smsRes.sms ?? []);
    } catch (err: unknown) {
      errors.push(
        err instanceof Error ? err.message : t("pro.system.capture.error.load"),
      );
    }
    setLoadError(errors.length ? errors[0] : null);
    setLoading(false);
    setRefreshing(false);
  }, [t]);

  React.useEffect(() => {
    void fetchAll();
    const interval = setInterval(() => void fetchAll(true), 5000);
    return () => clearInterval(interval);
  }, [fetchAll]);

  const selectedEmail = React.useMemo(() => {
    if (!selectedEmailKey) return null;
    const idx = emails.findIndex((e, i) => messageKey(e.sentAt, i) === selectedEmailKey);
    return idx >= 0 ? emails[idx] : null;
  }, [emails, selectedEmailKey]);

  const selectedSms = React.useMemo(() => {
    if (!selectedSmsKey) return null;
    const idx = sms.findIndex((m, i) => messageKey(m.sentAt, i) === selectedSmsKey);
    return idx >= 0 ? sms[idx] : null;
  }, [sms, selectedSmsKey]);

  React.useEffect(() => {
    if (emails.length === 0) {
      setSelectedEmailKey(null);
      return;
    }
    if (!selectedEmailKey || !emails.some((e, i) => messageKey(e.sentAt, i) === selectedEmailKey)) {
      setSelectedEmailKey(messageKey(emails[0].sentAt, 0));
    }
  }, [emails, selectedEmailKey]);

  React.useEffect(() => {
    if (sms.length === 0) {
      setSelectedSmsKey(null);
      return;
    }
    if (!selectedSmsKey || !sms.some((m, i) => messageKey(m.sentAt, i) === selectedSmsKey)) {
      setSelectedSmsKey(messageKey(sms[0].sentAt, 0));
    }
  }, [sms, selectedSmsKey]);

  const handleClear = React.useCallback(async () => {
    try {
      if (tab === "email") {
        await clearCapturedEmails();
        setEmails([]);
        setSelectedEmailKey(null);
      } else {
        await clearCapturedSms();
        setSms([]);
        setSelectedSmsKey(null);
      }
    } catch {
      // refresh will surface current state
    }
  }, [tab]);

  const activeMeta =
    tab === "email" ? selectedEmail?.meta : selectedSms?.meta;

  const count =
    tab === "email"
      ? t("pro.system.capture.emailCount", { count: emails.length })
      : t("pro.system.capture.messageCount", { count: sms.length });

  const sectionLayout = bindSectionLayout([
          {
            id: MESSAGES_SECTION_ID,
            label: t("pro.system.capture.messages"),
            icon: <Inbox size={16} />,
          },
        ]);

  return (
    <>
      <DetailPageLayout
        header={{
          media: <Inbox size={28} color={vars.color.fgMuted} />,
          title: t("pro.system.capture.title"),
          subtitle: t("pro.system.capture.subtitle"),
        }}
        tabs={
          <TabSwitcher
            aria-label={t("pro.system.capture.tabsAria")}
            tabs={[
              {
                value: "email",
                label: t("pro.system.capture.tab.email"),
                icon: <Mail size={14} />,
              },
              {
                value: "sms",
                label: t("pro.system.capture.tab.sms"),
                icon: <MessageSquare size={14} />,
              },
            ]}
            value={tab}
            onChange={(v) => setTab(v as CaptureTab)}
          />
        }
        contextActions={[
          {
            id: "refresh",
            label: t("pro.system.capture.refresh"),
            icon: <RefreshCw size={14} />,
            color: "neutral",
            onClick: () => void fetchAll(true),
            disabled: refreshing,
          },
          {
            id: "clear",
            label: t("pro.system.capture.clearAll"),
            icon: <Trash2 size={14} />,
            color: "danger",
            onClick: () => void handleClear(),
            disabled: tab === "email" ? emails.length === 0 : sms.length === 0,
          },
        ]}
        {...sectionLayout}
      >
        {loadError && (
          <div style={{ marginBottom: 16 }}>
            <Callout variant="error">{loadError}</Callout>
          </div>
        )}

        <CollapsibleSection
          id={MESSAGES_SECTION_ID}
          title={t("pro.system.capture.messages")}
          subtitle={count}
          icon={<Inbox size={18} />}
          expanded={isExpanded(MESSAGES_SECTION_ID)}
          onToggleExpanded={() => toggle(MESSAGES_SECTION_ID)}
          compactHeader
          flushBody
        >
          <div className={s.sectionBody}>
            <div className={s.messageList}>
              {loading &&
                (tab === "email" ? emails.length === 0 : sms.length === 0) && (
                  <div className={s.emptyState}>
                    {t("pro.system.capture.loading")}
                  </div>
                )}

              {!loading && tab === "email" && emails.length === 0 && (
                <div className={s.emptyState}>
                  <span>{t("pro.system.capture.noEmails")}</span>
                  <span>{t("pro.system.capture.noEmailsHint")}</span>
                </div>
              )}

              {!loading && tab === "sms" && sms.length === 0 && (
                <div className={s.emptyState}>
                  <span>{t("pro.system.capture.noSms")}</span>
                  <span>{t("pro.system.capture.noSmsHint")}</span>
                </div>
              )}

              {tab === "email" &&
                emails.map((email, idx) => {
                  const key = messageKey(email.sentAt, idx);
                  return (
                    <button
                      key={key}
                      type="button"
                      className={clsx(
                        s.messageCard,
                        selectedEmailKey === key && s.messageCardActive,
                      )}
                      onClick={() => setSelectedEmailKey(key)}
                    >
                      <span className={s.messageCardTo}>{email.to}</span>
                      <span className={s.messageCardSubject}>{email.subject}</span>
                      <div className={s.messageCardBadges}>
                        <span className={s.dateBadge}>{formatTime(email.sentAt)}</span>
                      </div>
                    </button>
                  );
                })}

              {tab === "sms" &&
                sms.map((msg, idx) => {
                  const key = messageKey(msg.sentAt, idx);
                  return (
                    <button
                      key={key}
                      type="button"
                      className={clsx(
                        s.messageCard,
                        selectedSmsKey === key && s.messageCardActive,
                      )}
                      onClick={() => setSelectedSmsKey(key)}
                    >
                      <span className={s.messageCardTo}>{formatPhone(msg.to)}</span>
                      <span className={s.messageCardSubject}>
                        {truncateBody(msg.body)}
                      </span>
                      <div className={s.messageCardBadges}>
                        <span className={s.dateBadge}>{formatTime(msg.sentAt)}</span>
                      </div>
                    </button>
                  );
                })}
            </div>

            <div className={s.detailPane}>
              {tab === "email" && !selectedEmail && (
                <div className={s.emptyState}>
                  {t("pro.system.capture.selectEmail")}
                </div>
              )}
              {tab === "sms" && !selectedSms && (
                <div className={s.emptyState}>
                  {t("pro.system.capture.selectMessage")}
                </div>
              )}

              {tab === "email" && selectedEmail && (
                <>
                  <div className={s.detailHeader}>
                    <div className={s.detailMetaRow}>
                      <div className={s.detailMetaMain}>
                        <div className={s.detailField}>
                          <span className={s.detailLabel}>
                            {t("pro.system.capture.to")}
                          </span>
                          <span className={s.detailValue}>{selectedEmail.to}</span>
                        </div>
                        <div className={s.detailField}>
                          <span className={s.detailLabel}>
                            {t("pro.system.capture.subject")}
                          </span>
                          <span className={s.detailValue}>
                            {selectedEmail.subject}
                          </span>
                        </div>
                        <div className={s.detailField}>
                          <span className={s.detailLabel}>
                            {t("pro.system.capture.sent")}
                          </span>
                          <span className={s.detailValue}>
                            {formatTime(selectedEmail.sentAt)}
                          </span>
                        </div>
                      </div>
                      <div className={s.detailMetaActions}>
                        <button
                          type="button"
                          className={s.metaIconBtn}
                          aria-label={t("pro.system.capture.viewMeta")}
                          disabled={!selectedEmail.meta}
                          onClick={() => setMetaOpen(true)}
                        >
                          <Braces size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                  {selectedEmail.html ? (
                    <div className={s.detailBody}>
                      <iframe
                        title={t("pro.system.capture.emailPreview")}
                        srcDoc={`<base target="_blank">${selectedEmail.html}`}
                        sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                      />
                    </div>
                  ) : selectedEmail.text ? (
                    <div className={s.detailText}>{selectedEmail.text}</div>
                  ) : (
                    <div className={s.emptyState}>
                      {t("pro.system.capture.noContent")}
                    </div>
                  )}
                </>
              )}

              {tab === "sms" && selectedSms && (
                <>
                  <div className={s.detailHeader}>
                    <div className={s.detailMetaRow}>
                      <div className={s.detailMetaMain}>
                        <div className={s.detailField}>
                          <span className={s.detailLabel}>
                            {t("pro.system.capture.to")}
                          </span>
                          <span className={s.detailValue}>
                            {formatPhone(selectedSms.to)}
                          </span>
                        </div>
                        <div className={s.detailField}>
                          <span className={s.detailLabel}>
                            {t("pro.system.capture.sent")}
                          </span>
                          <span className={s.detailValue}>
                            {formatTime(selectedSms.sentAt)}
                          </span>
                        </div>
                      </div>
                      <div className={s.detailMetaActions}>
                        <button
                          type="button"
                          className={s.metaIconBtn}
                          aria-label={t("pro.system.capture.viewMeta")}
                          disabled={!selectedSms.meta}
                          onClick={() => setMetaOpen(true)}
                        >
                          <Braces size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className={s.detailText}>{selectedSms.body}</div>
                </>
              )}
            </div>
          </div>
        </CollapsibleSection>
      </DetailPageLayout>

      <Modal
        open={metaOpen}
        setOpen={setMetaOpen}
        minWidth="520px"
        maxWidth="720px"
        maxHeight="80vh"
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 16, padding: "4px 0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Braces size={18} style={{ color: vars.color.fgMuted }} />
            <Text level="h3" weight="bold">
              {t("pro.system.capture.metaTitle")}
            </Text>
          </div>
          {activeMeta ? (
            <div className={s.jsonShell}>
              <JsonView
                data={activeMeta}
                style={darkStyles}
                shouldExpandNode={(level: number) => level < 3}
              />
            </div>
          ) : (
            <Text level="body-small" style={{ color: vars.color.fgMuted }}>
              {t("pro.system.capture.noMeta")}
            </Text>
          )}
          {activeMeta && (
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setMetaOpen(false)}
              >
                {t("shared.common.cancel")}
              </Button>
            </div>
          )}
        </div>
      </Modal>
    </>
  );
}
